import { Link } from 'react-router-dom';
import { Home, Settings, LogOut, Shield, ChevronRight } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';

const ROLE_LABELS: Record<string, string> = {
  listing_agent: 'Listing agent',
  co_listing_agent: 'Co-listing agent',
  buyer_agent: "Buyer's agent",
  buyer: 'Buyer',
  seller: 'Seller',
  tc: 'Transaction coordinator',
};

export function ProfileMenu() {
  const role =
    (typeof window !== 'undefined' &&
      window.localStorage.getItem('discloser.demo.role')) ||
    'listing_agent';

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          className="w-7 h-7 rounded-full bg-brand-ink text-white text-[11px] font-meta font-semibold flex items-center justify-center hover:bg-brand-ink-soft transition-colors data-[state=open]:ring-2 data-[state=open]:ring-brand-ink/20"
          aria-label="Profile menu"
        >
          N
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[260px] p-0 rounded-2xl border-black/5 shadow-lg">
        {/* Identity */}
        <div className="px-4 py-3.5">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-brand-ink text-white text-xs font-meta font-semibold flex items-center justify-center shrink-0">
              N
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold truncate">Nim Silva</div>
              <div className="font-meta text-[11px] text-brand-text-3 truncate">
                nimchrisryansilva@gmail.com
              </div>
            </div>
            <Badge className="bg-brand-info-bg text-brand-info font-meta border-0 text-[10px] shrink-0">
              Pro
            </Badge>
          </div>
        </div>

        <Separator className="bg-black/5" />

        {/* Role indicator */}
        <div className="px-4 py-2.5">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-meta text-[10px] uppercase tracking-wider text-brand-text-3">
                Viewing as
              </div>
              <div className="text-sm font-medium mt-0.5">
                {ROLE_LABELS[role] ?? role}
              </div>
            </div>
            <Link
              to="/preview/c-add"
              className="font-meta text-[11px] text-brand-accent hover:underline"
            >
              Change
            </Link>
          </div>
        </div>

        <Separator className="bg-black/5" />

        {/* Nav links */}
        <nav className="py-1.5">
          <MenuLink to="/preview/c-home" icon={Home} label="My Properties" />
          <MenuLink to="/preview/c-settings" icon={Settings} label="Settings" />
          <MenuLink
            to="/preview/c-settings/security"
            icon={Shield}
            label="Security"
          />
        </nav>

        <Separator className="bg-black/5" />

        {/* Sign out */}
        <div className="py-1.5">
          <button className="w-full text-left px-4 py-2 flex items-center gap-2.5 text-sm text-brand-critical hover:bg-brand-critical-bg/40 transition-colors">
            <LogOut className="w-3.5 h-3.5" />
            Sign out
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function MenuLink({
  to,
  icon: Icon,
  label,
}: {
  to: string;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
}) {
  return (
    <Link
      to={to}
      className="flex items-center gap-2.5 px-4 py-2 text-sm text-brand-text-2 hover:bg-brand-paper-warm hover:text-brand-ink transition-colors"
    >
      <Icon className="w-3.5 h-3.5 text-brand-text-3" />
      <span className="flex-1">{label}</span>
      <ChevronRight className="w-3 h-3 text-brand-text-3 opacity-0 group-hover:opacity-100" />
    </Link>
  );
}
